/**
 * SoftPaywallGate
 *
 * Cohort 1 calculator soft-paywall. When `gated === true`, renders an
 * email-capture card instead of the calculator's result. Submitting unlocks
 * the visitor for every calculator on the site (localStorage flag) and
 * triggers a re-render so the original result is shown.
 *
 * Authenticated users never see this; integration is at the calculator-page
 * level via shouldGateCalcRun() in lib/softPaywall.
 */
import { useState } from "react";
import { Lock, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { captureSoftPaywall } from "@/lib/api";
import { markCalcUnlocked } from "@/lib/softPaywall";
import { track } from "@/lib/analytics";

export default function SoftPaywallGate({
    toolSlug,
    onUnlock,
}) {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [optIn, setOptIn] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !email.trim()) return;
        setSubmitting(true);
        try {
            await captureSoftPaywall({
                email: email.trim().toLowerCase(),
                first_name: firstName.trim() || null,
                marketing_opt_in: optIn,
                tool_slug: toolSlug || null,
                source: "calculator",
            });
            track("soft_paywall_unlocked", {
                tool_slug: toolSlug,
                opted_in: optIn,
            });
            markCalcUnlocked();
            toast.success("Unlocked — your result is ready.");
            onUnlock?.();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "We couldn't unlock the calculator. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="bl-card p-6 sm:p-8 border border-bl relative overflow-hidden"
            data-testid="soft-paywall-gate"
        >
            <div className="absolute inset-x-0 top-0 h-1 bg-[var(--bl-primary)]" />
            <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                    <Lock className="w-5 h-5" />
                </span>
                <div>
                    <h3 className="font-display font-semibold text-lg sm:text-xl text-bl leading-tight">
                        One more thing before we run this one.
                    </h3>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed max-w-md">
                        Your free calculators stay free — we just ask for an email so
                        we can send tips and follow-ups when they&apos;re useful.
                        Unlocks every calculator on the site instantly.
                    </p>
                </div>
            </div>

            <form onSubmit={onSubmit} className="mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input
                        type="text"
                        autoComplete="given-name"
                        placeholder="First name (optional)"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="bl-input"
                        data-testid="soft-paywall-firstname"
                    />
                    <input
                        type="email"
                        autoComplete="email"
                        required
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bl-input"
                        data-testid="soft-paywall-email"
                    />
                </div>

                <label className="flex items-start gap-2 text-xs text-bl-muted cursor-pointer mb-5">
                    <input
                        type="checkbox"
                        checked={optIn}
                        onChange={(e) => setOptIn(e.target.checked)}
                        className="mt-0.5 accent-[var(--bl-primary)]"
                        data-testid="soft-paywall-optin"
                    />
                    <span>
                        Send me occasional inspection tips and homeowner guidance.
                        One-click unsubscribe in every email.
                    </span>
                </label>

                <button
                    type="submit"
                    disabled={submitting}
                    className="bl-btn-primary justify-center gap-2 w-full sm:w-auto"
                    data-testid="soft-paywall-submit"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Unlocking…
                        </>
                    ) : (
                        <>
                            <Mail className="h-4 w-4" />
                            Unlock all calculators
                        </>
                    )}
                </button>

                <p className="mt-3 text-xs text-bl-muted inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[var(--bl-moss)]" />
                    No credit card. No spam. Cancel anytime.
                </p>
            </form>
        </div>
    );
}
